/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermissionSet,
  getAelEventsArray,
  JobVariationStatus,
} from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  rendererIconByName,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  AppRouter,
  DateTimeTypes,
  getFormStateSnapshotValue,
  getRouteInitData, getRouteParam,
  getRouteParentTitle, UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { manualPrimitiveTracker, RouteDataService } from '@gh/core-data';
import { TRANSLATION_TUPLE_EMPTY, TranslationTuple } from '@gh/core-mls';
import { WaitingService } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  GridDefinitionFactory,
  GridLayout,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { skip$, switchMap$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { JobCoListBusinessService } from '../../resources/job-co-list-business.service';
import {
  getEmptyJobCo,
  getJobCoListItemId,
  JobCoList,
  JobCoListItem,
  JobListItem,
} from '../../resources/job.model';

@Component({
  standalone: false,
  selector: 'gh-job-list-component',
  templateUrl: 'job-co-list.component.html',
})
export class JobCoListComponent implements OnInit {
  parentTitle: Maybe<TranslationTuple>;
  hasParent: boolean;
  gridDefinition: LayoutBasedGridDefinition;
  jobCo: JobCoList;
  jobTracker = manualPrimitiveTracker<Maybe<ID>>('jobId');

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private router: AppRouter,
              private routeData: RouteDataService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigationService: BuilderNavigationService,
              private waitingService: WaitingService,
              private businessService: JobCoListBusinessService) {
  }

  get jobId(): ID {
    return getRouteParam(this.route, 'jobId');
  }

  get isJobEditable(): boolean {
    const masterInfo = getFormStateSnapshotValue(this.jobCo.masterInfo);
    return isNotNil(this.jobId) && !masterInfo.isFinalized && !masterInfo.isNotProceeding;
  }

  ngOnInit(): void {
    const { gridLayout, jobCo } = getRouteInitData(this.route);

    const parentTitle = getRouteParentTitle(this.route);
    this.parentTitle = parentTitle || TRANSLATION_TUPLE_EMPTY;

    assign(this, { jobCo });
    this.buildGridDefinitions(gridLayout);

    const tracker = this.routeData.tracker(this.jobTracker);
    tracker.listen(this.route);
    tracker.filterChanges.pipe(
      // initially data was preloaded by initView
      skip$(1),
      switchMap$(([jobId]) => jobId
        ? this.businessService.get(jobId).pipe((this.waitingService.operator()))
        : [getEmptyJobCo()]))
      .subscribe((job) => {
        this.jobCo = job;
      });
  }

  onClone(co: JobCoListItem): void {
    this.router.navigateToChild(['/job-admin/jobs', this.jobId, 'change-orders', 'new'], {
      queryParams: { cloneFrom: co.id },
      parentTitle: 'lbl_job_change_orders',
    });
  }

  onCreate(): void {
    this.router.navigateToChild(['/job-admin/jobs', this.jobId, 'change-orders', 'new'], {
      parentTitle: 'lbl_job_change_orders',
    });
  }

  onJobNumberSelected(job: JobListItem): void {
    this.jobTracker.setValue(job ? job.id : void 0);
  }

  onOpen(co: JobCoListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobChangeOrders.EDIT_VIEW)) {
      this.router.navigateToChild(['/job-admin/jobs', this.jobId, 'change-orders', co.id], {
        parentTitle: 'lbl_job_change_orders',
      });
    }
  }

  goToChangeOrderListAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_job_change_orders',
      eventTypeIds: getAelEventsArray(AelEvents.Job.ChangeOrder),
      entityId: this.jobId,
      entityType: 'JOB',
    });
  }

  goToChangeOrderAel(changeOrderId: ID): void {
    this.navigationService.goToAel({
      label: 'lbl_job_change_orders',
      entityId: changeOrderId,
      entityType: 'JOB_VARIATION',
    });
  }

  private buildGridDefinitions(gridLayout: GridLayout): void {
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel({
      columns: {
        dateCreated: buildDateTimeColumn(DateTimeTypes.ShortDate),
        timeExtensionDays: {
          cellClass: 'text-center',
        },
        description: {
          suppressSizeToFit: false,
        },
        isDrawn: {
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
        status: {
          minWidth: 120,
          cellClass: ({ data }: { data: JobCoListItem }) => ({
            [JobVariationStatus.Quote]: 'cell-color-quote',
            [JobVariationStatus.Approved]: 'cell-color-approved',
            [JobVariationStatus.Reversed]: 'cell-color-reversed',
            [JobVariationStatus.Rejected]: 'cell-color-rejected',
          }[data.status]),
        },
      },
      model: createDefaultGridModel({
        getId: getJobCoListItemId,
      }),
      options: {
        getRowId: (params) => String(params.data.id)
      },
      layout: gridLayout,
    });
  }
}
